<script setup lang="ts">
import { v4 as uuidv4 } from 'uuid'
import { computed, onMounted, ref, watch } from 'vue'

import SancareOcticon from '../../components/SancareOcticon.vue'
import { ComponentSize, FindBarColor } from '../../components/types'

export interface SancareFindBarProps<T = unknown> {
  color?: FindBarColor
  fitPlaceholder?: boolean
  placeholder?: string
  results: T[]
  size?: ComponentSize
}

const props = withDefaults(defineProps<SancareFindBarProps>(), {
  color: FindBarColor.BLACK,
  fitPlaceholder: true,
  placeholder: '',
  size: ComponentSize.MEDIUM,
})

const emit = defineEmits<{
  (e: 'submit'): void
  (e: 'select', item: unknown): void
}>()

const model = defineModel<string>({ required: true })

const showResults = ref(false)
const inputEl = ref<HTMLInputElement | null>(null)
// Trick to get the width of the placeholder
const mirrorElement = ref<HTMLSpanElement | null>(null)

const resultsWithId = computed(() =>
  (props.results ?? []).map((item) => {
    if (item && typeof item === 'object') {
      return { ...item, _uid: uuidv4() }
    } else {
      return { value: item, _uid: uuidv4() }
    }
  })
)

function onKeyup(e: KeyboardEvent) {
  if (e.key === 'Enter') emit('submit')
  if (e.key === 'Escape') showResults.value = false
}

function onSelect(item: unknown) {
  emit('select', item)
  showResults.value = false
}

const onFocus = () => showResults.value = true
const close = () => showResults.value = false
const focusInput = () => inputEl.value?.focus()

function updateWidth(): void {
  if (!props.fitPlaceholder) return
  if (!inputEl.value || !mirrorElement.value) return

  mirrorElement.value.textContent = props.placeholder || ' '
  // 16 = icon width
  const width = mirrorElement.value.offsetWidth + 16

  inputEl.value.style.width = `${width}px`
}

watch(
  () => props.placeholder,
  () => requestAnimationFrame(updateWidth),
  { immediate: true }
)

onMounted(() => {
  requestAnimationFrame(updateWidth)
})

</script>

<template>
  <div
    v-on-click-outside="close"
    class="sancare-find-bar position-relative"
  >
    <div
      class="sancare-find-bar__search-bar d-inline-flex align-items-center gap-2"
      :class="{
        'sancare-find-bar__search-bar--fit-placeholder': fitPlaceholder,
        'sancare-find-bar__search-bar--full-width': !fitPlaceholder,
        'sancare-find-bar__search-bar--purple': color === FindBarColor.PURPLE,
        'px-2 py-1': size === ComponentSize.SMALL,
        'px-3 py-2': size === ComponentSize.MEDIUM,
      }"
      @click="focusInput"
    >
      <sancare-octicon
        class="sancare-find-bar__search-bar__icon"
        :class="{ 'sancare-find-bar__search-bar__icon--purple': color === FindBarColor.PURPLE }"
        name="search"
      />
      <input
        ref="inputEl"
        v-model="model"
        :placeholder="placeholder"
        class="sancare-find-bar__search-bar__input form-control"
        :class="{ 'sancare-find-bar__search-bar__input--purple': color === FindBarColor.PURPLE }"
        type="text"
        @keyup="onKeyup"
        @focus="onFocus"
      >
      <span
        ref="mirrorElement"
        class="sancare-find-bar__search-bar__mirror"
      >
        {{ placeholder }}
      </span>
    </div>

    <div
      v-if="showResults && resultsWithId.length"
      class="sancare-find-bar__search-results list-group position-absolute w-100"
    >
      <button
        v-for="item in resultsWithId"
        :key="item._uid"
        class="sancare-find-bar__search-results__items list-group-item list-group-item-action"
        @click="onSelect(item)"
      >
        <slot
          name="result"
          :item="item"
        >
          {{ item }}
        </slot>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@sancare/typescript-commons/src/assets/styles/variables';

.sancare-find-bar {
  &__search-bar {
    border: 1px solid #dee2e6;
    border-radius: $border-radius-xs;
    max-width: 100%;

    &:focus-within {
      border-color: #666cf7;
      box-shadow: 0 0 0 .2rem rgba(0, 16, 255, .25);
    }

    &__icon {
      fill: inherit !important;
      flex-shrink: 0;

      &--purple {
        fill: #666cf7 !important;
      }
    }

    &__input {
      border: none;
      background-color: transparent;
      font-size: 1rem;
      padding: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;

      &--purple::placeholder {
        color: $sancare-light-purple;
      }
    }

    &--full-width {
      width: 100%;
    }

    &--fit-placeholder {
      width: auto;
      max-width: 100%;

      input {
        width: auto;
        min-width: 8ch;
        max-width: 100%;
      }
    }

    &--purple {
      border-color: #c3c4d9;
    }

    &__mirror {
      position: absolute;
      visibility: hidden;
      white-space: nowrap;
      padding: 0;
    }
  }

  &__search-results {
    max-height: 500px;
    overflow-y: auto;
    top: 100%;
    background: #ffffff;
    z-index: $z-index-dropdown;
    box-shadow: 0 5px 6px rgba(0,0,0,.6);

    &__items {
      color: initial;
    }
  }
}
</style>
